import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { User } from '@models/user';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { LoaderService } from '@services/loader.service';
import { ShopService } from '@services/shop.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonRendererComponent } from '@ui/components/atlas-grid/renderer/button-renderer.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { AtlasGridTranslateRendererComponent } from '@ui/components/atlas-grid/renderer/translate-renderer.component';
import { formateDateTime, getCurrencySymbol } from '@utils/utils';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-billing-orders',
  templateUrl: './billing-orders.component.html',
  styleUrls: ['./billing-orders.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class BillingOrdersComponent implements OnInit, AfterViewInit {
  @Input() user: User;
  @ViewChild('gridInvoices') gridInvoices: AtlasGridComponent;
  @ViewChild('modalCart') modalCart;
  @ViewChild('modalDelete') modalDelete;
  modalDeleteModal: NgbModalRef;
  modalCartModal: NgbModalRef;
  gridAnimationState = 'hide';

  tableInvoicesGridOptions;
  tableInvoicesLoading: boolean;
  tableInvoicesColumnDefs: any[];
  components = {
    buttonRenderer: ButtonRendererComponent,
    buttonGroupRenderer: ButtonGroupRendererComponent,
    translateRenderer: AtlasGridTranslateRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  paginationPageSize = 10;

  getCurrencySymbol = getCurrencySymbol;

  cartToView;
  cartToDelete;
  constructor(
    private shopService: ShopService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private atlasToastService: AtlasToastService
  ) {}
  ngAfterViewInit(): void {
    this.gridInvoices.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
  }

  ngOnInit(): void {
    this.initInvoicesTab();
  }

  initInvoicesTab() {
    this.tableInvoicesGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      resizable: false,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadListInvoices.bind(this)
    });
    this.tableInvoicesColumnDefs = [
      {
        headerNameTranslate: 'shop_history_tablefield_order',
        field: 'idShoppingCart',
        sortable: false
      },
      {
        headerNameTranslate: 'common_date',
        field: 'createDate',
        cellRenderer: 'htmlRenderer',
        sortable: false,
        cellRendererParams: {
          content: params => (params && params.data ? formateDateTime(params.data.lastModifyDate, false) : '')
        }
      },
      {
        headerNameTranslate: 'shop_cart_grandtotal',
        field: 'totalPriceToPay',
        cellRenderer: 'htmlRenderer',
        sortable: false,
        cellRendererParams: {
          content: params => (params && params.data ? getCurrencySymbol() + ' ' + params.data.totalPriceToPay : '')
        }
      },
      {
        headerNameTranslate: 'shop_history_tablefield_payment',
        field: 'paymentMethod',
        sortable: false,
        cellRenderer: 'translateRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? 'shop_history_payment_' + params.data.paymentMethod : '')
        }
      },
      {
        headerNameTranslate: 'common_status',
        field: 'status',
        sortable: false,
        cellRenderer: 'translateRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? 'shop_history_cart_status_' + params.data.status : '')
        }
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.openModalCart(e),
              label: 'common_view'
            },
            {
              onClick: e => this.downloadPdf(e),
              label: 'shop_history_downloadinvoice',
              hide: data => {
                if (!data) {
                  return true;
                } else {
                  return !data.invoiced;
                }
              }
            },
            {
              onClick: e => this.openModalDelete(e),
              label: 'common_delete',
              hide: data => {
                if (!data) {
                  return true;
                } else {
                  return data.status !== 2;
                }
              }
            }
          ]
        },
        maxWidth: 82
      }
    ];
  }

  reloadListInvoices(startRow?, endRow?, sort?) {
    this.loaderService.show();
    return this.shopService.getCarts(1 + startRow / (endRow - startRow), endRow - startRow).pipe(finalize(() => this.loaderService.hide()));
  }

  openModalCart(data: any): void {
    this.cartToView = data;
    this.modalCartModal = this.modalService.open(this.modalCart);
    this.modalCartModal.result.then(
      result => {},
      reason => {}
    );
  }

  downloadPdf(data) {
    this.shopService.getInvoiceHtml(data.idShoppingCart).subscribe(
      d => {
        const matches = d.body.match(/name="([^"]+)" src="([^"]+)"/);
        if (matches) {
          this.downloadPDFBase(matches[2], matches[1]);
        }
      },
      () => {
        this.atlasToastService.error('common_error', null);
      },
      () => {}
    );
  }

  downloadPDFBase(pdf, fileName) {
    const downloadLink = document.createElement('a');
    downloadLink.href = pdf;
    downloadLink.download = fileName.replace('.html', '.pdf');
    downloadLink.click();
  }

  openModalDelete(data: any): void {
    this.cartToDelete = data;
    this.modalDeleteModal = this.modalService.open(this.modalDelete);
    this.modalDeleteModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteForm(): void {
    this.shopService.deleteCart(this.cartToDelete.idShoppingCart).subscribe(
      () => {
        this.atlasToastService.success('common_success', null);
        this.gridInvoices.refresh();
      },
      () => {
        this.atlasToastService.error('common_error', null);

        this.gridInvoices.refresh();
      },
      () => {}
    );
    this.modalDeleteModal.close();
  }

  replaceLabelTagWithValue(cartItem) {
    let label: string = this.translateService.instant(cartItem.shopItem.label);

    const splitChart = this.user.nation === 'gbr' ? '.' : ',';

    label = label.replace('$1$', cartItem?.serviceParams?.ratedSms);
    label = label.replace('$2$', (cartItem.shopItem.price as string).split(splitChart)[0]);

    return label;
  }
}
